import { useState, useEffect, useRef } from 'react'
import { 
  FileText, Search, MapPin, Clock, CheckCircle, AlertCircle, 
  XCircle, Play, RefreshCw, Radio, Upload, Loader2, X, File
} from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface ScraperStatus {
  territoryId: number
  territoryName: string
  scraperEnabled: boolean
  lastScrapedAt: string | null
  hoursSinceScrape: number | null
  status: 'healthy' | 'stale' | 'never_scraped' | 'disabled' | 'no_scraper'
  applicationCount: number
  newCount: number
}

interface Application {
  id: number
  referenceNumber: string
  propertyAddress: string
  applicantName: string | null
  description: string | null
  decision: string | null
  decisionDate: string | null
  status: string
  territoryId: number
  scrapedAt: string
}

interface Stats {
  total: number
  new: number
  classified: number
  assigned: number
  processed: number
  skipped: number
  error: number
  byTerritory: Record<string, number>
}

interface Territory {
  id: number
  name: string
}

interface QueuedTask {
  id: string
  territoryId: number
  territoryName: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  queuedAt: string
  startedAt: string | null
  completedAt: string | null
  error: string | null
}

type TabType = 'scrapers' | 'queue' | 'applications'

export default function Applications() {
  const [activeTab, setActiveTab] = useState<TabType>('scrapers')
  const [scraperStatuses, setScraperStatuses] = useState<ScraperStatus[]>([])
  const [applications, setApplications] = useState<Application[]>([])
  const [territories, setTerritories] = useState<Territory[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [runningScrapers, setRunningScrapers] = useState<Set<number>>(new Set())
  const [queuedTasks, setQueuedTasks] = useState<QueuedTask[]>([])
  
  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadTerritoryId, setUploadTerritoryId] = useState<number | null>(null)
  const [uploadFiles, setUploadFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // Filters
  const [selectedTerritory, setSelectedTerritory] = useState<number | 'all'>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (activeTab === 'applications' && !loading) {
      fetchApplications()
    }
  }, [activeTab, selectedTerritory, selectedStatus, loading])

  const fetchData = async () => {
    try {
      const [scraperRes, statsRes, territoriesRes] = await Promise.all([
        api.get('/api/v1/applications/scrapers/status'),
        api.get('/api/v1/applications/stats'),
        api.get('/api/v1/territories/?limit=500'),
      ])
      setScraperStatuses(scraperRes.data)
      setStats(statsRes.data)
      setTerritories(territoriesRes.data)
    } catch (error) {
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const fetchApplications = async () => {
    try {
      let url = '/api/v1/applications/?limit=100'
      if (selectedTerritory !== 'all') {
        url += `&territory_id=${selectedTerritory}`
      }
      if (selectedStatus !== 'all') {
        url += `&app_status=${selectedStatus}`
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`
      }
      const response = await api.get(url)
      setApplications(response.data)
    } catch (error) {
      toast.error('Failed to load applications')
    }
  }

  const runScraper = async (territoryId: number) => {
    const territory = scraperStatuses.find(s => s.territoryId === territoryId)
    setRunningScrapers(prev => new Set(prev).add(territoryId))
    try {
      const response = await api.post(`/api/v1/applications/scrapers/run/${territoryId}`)
      toast.success('Scraper queued successfully')

      // Add to queued tasks
      const newTask: QueuedTask = {
        id: response.data.taskId,
        territoryId: territoryId,
        territoryName: territory?.territoryName || 'Unknown',
        status: 'queued',
        queuedAt: new Date().toISOString(),
        startedAt: null,
        completedAt: null,
        error: null,
      }
      setQueuedTasks(prev => [newTask, ...prev])
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to run scraper')
    } finally {
      setRunningScrapers(prev => {
        const next = new Set(prev)
        next.delete(territoryId)
        return next
      })
    }
  }

  const runAllScrapers = async () => {
    try {
      const response = await api.post('/api/v1/applications/scrapers/run-all')
      toast.success('All scrapers queued')

      // Add placeholder task for bulk run
      const newTask: QueuedTask = {
        id: response.data.taskId,
        territoryId: 0,
        territoryName: 'All Enabled Territories',
        status: 'queued',
        queuedAt: new Date().toISOString(),
        startedAt: null,
        completedAt: null,
        error: null,
      }
      setQueuedTasks(prev => [newTask, ...prev])
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to run scrapers')
    }
  }

  const clearCompletedTasks = () => {
    setQueuedTasks(prev => prev.filter(t => t.status === 'queued' || t.status === 'running'))
  }

  const getStatusBadge = (status: ScraperStatus['status']) => {
    switch (status) {
      case 'healthy':
        return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', icon: CheckCircle, label: 'Healthy' }
      case 'stale':
        return { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', icon: Clock, label: 'Stale' }
      case 'never_scraped':
        return { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', icon: Clock, label: 'Never Run' }
      case 'disabled':
        return { bg: 'bg-slate-500/20', text: 'text-slate-400', border: 'border-slate-500/30', icon: XCircle, label: 'Disabled' }
      case 'no_scraper':
        return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', icon: AlertCircle, label: 'No Scraper' }
      default:
        return { bg: 'bg-slate-500/20', text: 'text-slate-400', border: 'border-slate-500/30', icon: AlertCircle, label: 'Unknown' }
    }
  }

  const formatTimeAgo = (hours: number | null) => {
    if (hours === null) return 'Never'
    if (hours < 1) return 'Just now'
    if (hours < 24) return `${Math.round(hours)}h ago`
    const days = Math.round(hours / 24)
    return `${days}d ago`
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'))
    if (pdfFiles.length !== files.length) {
      toast.error('Only PDF files are allowed')
    }
    setUploadFiles(prev => [...prev, ...pdfFiles])
  }

  const removeFile = (index: number) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (!uploadTerritoryId || uploadFiles.length === 0) {
      toast.error('Please select a territory and at least one PDF file')
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('territory_id', uploadTerritoryId.toString())
      uploadFiles.forEach(file => {
        formData.append('files', file)
      })

      const response = await api.post('/api/v1/applications/upload-manual', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      if (response.data.success > 0) {
        toast.success(`Successfully uploaded ${response.data.success} application(s)`)
      }
      if (response.data.failed > 0) {
        toast.error(`Failed to upload ${response.data.failed} file(s)`)
        // Upload errors are shown via toast above
      }

      // Reset and close modal
      setUploadFiles([])
      setUploadTerritoryId(null)
      setShowUploadModal(false)
      
      // Refresh data
      fetchData()
      if (activeTab === 'applications') {
        fetchApplications()
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-slate-100">Planning Applications</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Upload PDFs
          </button>
          <button
            onClick={runAllScrapers}
            className="btn-primary flex items-center gap-2"
          >
            <Play className="w-4 h-4" />
            Run All Scrapers
          </button>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <div className="card p-4">
          <p className="text-2xl font-bold text-slate-100">{stats?.total || 0}</p>
          <p className="text-xs text-slate-400">Total</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-blue-400">{stats?.new || 0}</p>
          <p className="text-xs text-slate-400">New</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-purple-400">{stats?.classified || 0}</p>
          <p className="text-xs text-slate-400">Classified</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-amber-400">{stats?.assigned || 0}</p>
          <p className="text-xs text-slate-400">Assigned</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-emerald-400">{stats?.processed || 0}</p>
          <p className="text-xs text-slate-400">Processed</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-slate-400">{stats?.skipped || 0}</p>
          <p className="text-xs text-slate-400">Skipped</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-red-400">{stats?.error || 0}</p>
          <p className="text-xs text-slate-400">Errors</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-800/50 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('scrapers')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'scrapers'
              ? 'bg-[#f39c13] text-white'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          Scraper Status
        </button>
        <button
          onClick={() => setActiveTab('queue')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'queue'
              ? 'bg-[#f39c13] text-white'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          Queue
          {queuedTasks.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs rounded-full bg-blue-500 text-white">
              {queuedTasks.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('applications')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'applications'
              ? 'bg-[#f39c13] text-white'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          Applications
        </button>
      </div>

      {activeTab === 'scrapers' && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 bg-slate-800/30 border-b border-slate-700/50 flex justify-between items-center">
            <span className="text-sm text-slate-400">
              {scraperStatuses.filter(s => s.status === 'healthy').length} healthy, {' '}
              {scraperStatuses.filter(s => s.scraperEnabled).length} enabled
            </span>
            <button onClick={fetchData} className="text-slate-400 hover:text-slate-200">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <table className="w-full">
            <thead className="bg-slate-800/50 border-b border-slate-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Territory</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Last Run</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Applications</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">New</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {scraperStatuses
                .filter(s => s.scraperEnabled || s.status !== 'no_scraper')
                .map((scraper) => {
                  const badge = getStatusBadge(scraper.status)
                  const BadgeIcon = badge.icon
                  const isRunning = runningScrapers.has(scraper.territoryId)

                  return (
                    <tr key={scraper.territoryId} className="hover:bg-slate-800/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-200">{scraper.territoryName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full ${badge.bg} ${badge.text} border ${badge.border}`}>
                          <BadgeIcon className="w-3 h-3" />
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-400">
                        {formatTimeAgo(scraper.hoursSinceScrape)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {scraper.applicationCount}
                      </td>
                      <td className="px-4 py-3">
                        {scraper.newCount > 0 ? (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/20 text-blue-400">
                            {scraper.newCount}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-500">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {scraper.status !== 'no_scraper' && (
                          <button
                            onClick={() => runScraper(scraper.territoryId)}
                            disabled={isRunning}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-700 text-slate-200 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {isRunning ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Play className="w-3 h-3" />
                            )}
                            {isRunning ? 'Running...' : 'Run'}
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>

          {scraperStatuses.filter(s => s.scraperEnabled || s.status !== 'no_scraper').length === 0 && (
            <div className="text-center py-12">
              <Radio className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-200 mb-2">No scrapers configured</h3>
              <p className="text-slate-400">Enable scrapers for territories with clients.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'queue' && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 bg-slate-800/30 border-b border-slate-700/50 flex justify-between items-center">
            <span className="text-sm text-slate-400">
              {queuedTasks.filter(t => t.status === 'queued').length} queued, {' '}
              {queuedTasks.filter(t => t.status === 'running').length} running
            </span>
            {queuedTasks.some(t => t.status === 'completed' || t.status === 'failed') && (
              <button 
                onClick={clearCompletedTasks}
                className="text-sm text-slate-400 hover:text-slate-200"
              >
                Clear completed
              </button>
            )}
          </div>
          
          {queuedTasks.length > 0 ? (
            <table className="w-full">
              <thead className="bg-slate-800/50 border-b border-slate-700/50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Task ID</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Territory</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Queued At</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {queuedTasks.map((task) => (
                  <tr key={task.id} className="hover:bg-slate-800/30">
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-slate-400">{task.id.slice(0, 8)}...</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-200">{task.territoryName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full ${
                        task.status === 'queued' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                        task.status === 'running' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                        task.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                        'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {task.status === 'running' && <RefreshCw className="w-3 h-3 animate-spin" />}
                        {task.status === 'queued' && <Clock className="w-3 h-3" />}
                        {task.status === 'completed' && <CheckCircle className="w-3 h-3" />}
                        {task.status === 'failed' && <XCircle className="w-3 h-3" />}
                        {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-400">
                      {new Date(task.queuedAt).toLocaleTimeString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {task.error ? (
                        <span className="text-red-400">{task.error}</span>
                      ) : task.status === 'queued' ? (
                        'Waiting for worker...'
                      ) : task.status === 'running' ? (
                        'Processing...'
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-200 mb-2">No queued tasks</h3>
              <p className="text-slate-400">Run a scraper to see tasks appear here.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'applications' && (
        <>
          {/* Filters */}
          <div className="card p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchApplications()}
                  placeholder="Search applications..."
                  className="input pl-10 w-full"
                />
              </div>
              <select
                value={selectedTerritory}
                onChange={(e) => setSelectedTerritory(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                className="input py-2 px-3 w-auto"
              >
                <option value="all">All Territories</option>
                {territories.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="input py-2 px-3 w-auto"
              >
                <option value="all">All Statuses</option>
                <option value="new">New</option>
                <option value="classified">Classified</option>
                <option value="assigned">Assigned</option>
                <option value="processed">Processed</option>
                <option value="skipped">Skipped</option>
                <option value="error">Error</option>
              </select>
              <button onClick={fetchApplications} className="btn-secondary">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Applications List */}
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-800/50 border-b border-slate-700/50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Reference</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Property</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Description</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Scraped</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {applications.map((app) => (
                  <tr key={app.id} className="hover:bg-slate-800/30">
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono text-[#f39c13]">{app.referenceNumber}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-200 line-clamp-2">{app.propertyAddress}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-400 line-clamp-2">{app.description || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
                        app.status === 'new' ? 'bg-blue-500/20 text-blue-400' :
                        app.status === 'processed' ? 'bg-emerald-500/20 text-emerald-400' :
                        app.status === 'error' ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {app.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-400">
                      {new Date(app.scrapedAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {applications.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-200 mb-2">No applications found</h3>
                <p className="text-slate-400">Run a scraper to fetch planning applications.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card w-full max-w-lg mx-4">
            <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
              <h3 className="font-medium text-slate-200">Upload Planning Applications</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false)
                  setUploadFiles([])
                  setUploadTerritoryId(null)
                }}
                className="text-slate-400 hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Territory Selection */}
              <div>
                <label className="label">Territory / County</label>
                <select
                  value={uploadTerritoryId || ''}
                  onChange={(e) => setUploadTerritoryId(e.target.value ? Number(e.target.value) : null)}
                  className="input w-full"
                >
                  <option value="">Select a territory...</option>
                  {territories.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              {/* File Drop Zone */}
              <div>
                <label className="label">PDF Files</label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-slate-500 transition-colors"
                >
                  <Upload className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                  <p className="text-slate-300 mb-1">Click to select PDF files</p>
                  <p className="text-xs text-slate-500">or drag and drop</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Selected Files List */}
              {uploadFiles.length > 0 && (
                <div>
                  <label className="label">{uploadFiles.length} file(s) selected</label>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {uploadFiles.map((file, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-slate-800/50 rounded px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <File className="w-4 h-4 text-red-400 flex-shrink-0" />
                          <span className="text-sm text-slate-300 truncate">{file.name}</span>
                        </div>
                        <button
                          onClick={() => removeFile(idx)}
                          className="text-slate-500 hover:text-red-400 flex-shrink-0 ml-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-4 py-3 border-t border-slate-700/50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowUploadModal(false)
                  setUploadFiles([])
                  setUploadTerritoryId(null)
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !uploadTerritoryId || uploadFiles.length === 0}
                className="btn-primary flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload {uploadFiles.length > 0 ? `${uploadFiles.length} Files` : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
